import React, { useState, useEffect, useCallback } from 'react';
import { Plant, Cell, Conflict } from '../types';
import { getRotationStatus, getRotationReason } from '../utils/rotation';
import { plantGlyph } from '../utils/plantGlyph';
import PlantPicker from './PlantPicker';
import ConflictToast from './ConflictToast';
import './GardenGrid.css';

/** Tracks whether we're on a mobile-width screen. */
function useIsMobile() {
  const query = '(max-width: 768px)';
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

type Props = {
  widthFt: number;
  heightFt: number;
  plants: Plant[];
  cells: Cell[];
  previousCells: Cell[];
  onCellsChange: (cells: Cell[]) => void;
};

// Only reports conflicts that involve the plant just placed — mirrors
// getNewCompanions so the toast doesn't blame a new plant for pre-existing
// conflicts elsewhere in the bed (e.g. an existing onion↔peas pairing).
function getNewConflicts(newPlant: Plant, previousCells: Cell[], plants: Plant[]): Conflict[] {
  const plantMap = new Map(plants.map(p => [p.plantId, p]));
  const previousIds = Array.from(new Set(previousCells.filter(c => c.plantId).map(c => c.plantId as string)));
  const conflicts: Conflict[] = [];
  const seen = new Set<string>();

  for (const otherId of previousIds) {
    if (otherId === newPlant.plantId) continue;
    const other = plantMap.get(otherId);
    if (!other) continue;
    const key = [newPlant.plantId, otherId].sort().join('|');
    if (seen.has(key)) continue;
    if (newPlant.antagonists.includes(otherId)) {
      conflicts.push({ plant1: newPlant, plant2: other, reason: newPlant.antagonistReasons[otherId] });
      seen.add(key);
    } else if (other.antagonists.includes(newPlant.plantId)) {
      conflicts.push({ plant1: other, plant2: newPlant, reason: other.antagonistReasons[newPlant.plantId] });
      seen.add(key);
    }
  }
  return conflicts;
}

function getNewCompanions(newPlant: Plant, previousCells: Cell[], plants: Plant[]): { plant1: Plant; plant2: Plant; reason: string }[] {
  const plantMap = new Map(plants.map(p => [p.plantId, p]));
  const previousIds = Array.from(new Set(previousCells.filter(c => c.plantId).map(c => c.plantId as string)));
  const companions: { plant1: Plant; plant2: Plant; reason: string }[] = [];
  const seen = new Set<string>();

  for (const otherId of previousIds) {
    if (otherId === newPlant.plantId) continue;
    const other = plantMap.get(otherId);
    if (!other) continue;
    const key = [newPlant.plantId, otherId].sort().join('|');
    if (seen.has(key)) continue;
    if (newPlant.companions.includes(otherId) || other.companions.includes(newPlant.plantId)) {
      // Reason may be declared on either plant (relationship is bidirectional).
      const reason = newPlant.companionReasons[otherId] || other.companionReasons[newPlant.plantId] || '';
      companions.push({ plant1: newPlant, plant2: other, reason });
      seen.add(key);
    }
  }
  return companions;
}

export default function GardenGrid({ widthFt, heightFt, plants, cells, previousCells, onCellsChange }: Props) {
  const [pickerCell, setPickerCell] = useState<{ x: number; y: number } | null>(null);
  const [toastConflicts, setToastConflicts] = useState<Conflict[]>([]);
  const [toastCompanions, setToastCompanions] = useState<{ plant1: Plant; plant2: Plant; reason: string }[]>([]);
  const [toastRotation, setToastRotation] = useState<{ plant: Plant; previousPlant: Plant; reason: string; status: 'good' | 'bad' }[]>([]);

  const plantMap = new Map(plants.map(p => [p.plantId, p]));
  const prevCellMap = new Map(previousCells.map(c => [`${c.x},${c.y}`, c]));

  const getCell = (x: number, y: number) => cells.find(c => c.x === x && c.y === y) ?? { x, y, plantId: null };
  const getPreviousPlant = (x: number, y: number): Plant | null => {
    const prev = prevCellMap.get(`${x},${y}`);
    return prev?.plantId ? (plantMap.get(prev.plantId) ?? null) : null;
  };

  const handleCellClick = (x: number, y: number) => {
    // Clear any previous advice as soon as another square is tapped
    setToastConflicts([]);
    setToastCompanions([]);
    setToastRotation([]);
    setPickerCell({ x, y });
  };

  const handlePlantSelect = useCallback((plant: Plant | null) => {
    if (!pickerCell) return;
    const previousCurrentCells = cells.filter(c => !(c.x === pickerCell.x && c.y === pickerCell.y));
    const updated = plant
      ? [...previousCurrentCells, { x: pickerCell.x, y: pickerCell.y, plantId: plant.plantId }]
      : previousCurrentCells;

    onCellsChange(updated);
    setPickerCell(null);

    if (plant) {
      const conflicts = getNewConflicts(plant, previousCurrentCells, plants);
      const companions = getNewCompanions(plant, previousCurrentCells, plants);

      // Check rotation for this cell
      const prevPlant = getPreviousPlant(pickerCell.x, pickerCell.y);
      const rotationNotes: { plant: Plant; previousPlant: Plant; reason: string; status: 'good' | 'bad' }[] = [];
      if (prevPlant) {
        const rotStatus = getRotationStatus(plant, prevPlant);
        if (rotStatus === 'good' || rotStatus === 'bad') {
          rotationNotes.push({
            plant,
            previousPlant: prevPlant,
            reason: getRotationReason(plant, prevPlant),
            status: rotStatus,
          });
        }
      }

      setToastConflicts(conflicts);
      setToastCompanions(companions);
      setToastRotation(rotationNotes);
    }
  }, [pickerCell, cells, plants, previousCells, onCellsChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDismissToast = useCallback(() => {
    setToastConflicts([]);
    setToastCompanions([]);
    setToastRotation([]);
  }, []);

  const currentCell = pickerCell ? getCell(pickerCell.x, pickerCell.y) : null;
  const pickerPreviousPlant = pickerCell ? getPreviousPlant(pickerCell.x, pickerCell.y) : null;

  const plantedPlants = cells
    .filter(c => c.plantId && !(pickerCell && c.x === pickerCell.x && c.y === pickerCell.y))
    .map(c => plantMap.get(c.plantId!))
    .filter((p): p is Plant => p !== undefined);

  const hasPreviousCells = previousCells.some(c => c.plantId);

  // On mobile, keep the grid portrait: orient it so the smaller dimension
  // runs along the top. The underlying cell data (x,y) is unchanged — only
  // the visual rows/columns are swapped.
  const isMobile = useIsMobile();
  const transposed = isMobile && widthFt > heightFt;
  const displayCols = transposed ? heightFt : widthFt;
  const displayRows = transposed ? widthFt : heightFt;
  const toActual = (dc: number, dr: number) =>
    transposed ? { x: dr, y: dc } : { x: dc, y: dr };

  return (
    <div className="grid-wrapper" style={{ '--cols': displayCols, '--rows': displayRows } as React.CSSProperties}>
      {hasPreviousCells && (
        <p className="grid__rotation-hint">🔄 Faded plants show last season — plan your rotation</p>
      )}
      <div className="grid-wrapper__inner">
        <div className="grid-labels grid-labels--col">
          {Array.from({ length: displayRows }, (_, i) => (
            <span key={i} className="grid-label">{i + 1}</span>
          ))}
        </div>
        <div className="grid-inner">
          <div className="grid-labels grid-labels--row">
            {Array.from({ length: displayCols }, (_, i) => (
              <span key={i} className="grid-label">{i + 1}</span>
            ))}
          </div>
          <div
            className="grid"
            style={{
              gridTemplateColumns: `repeat(${displayCols}, 1fr)`,
              gridTemplateRows: `repeat(${displayRows}, 1fr)`,
            }}
          >
            {Array.from({ length: displayRows }, (_, dr) =>
              Array.from({ length: displayCols }, (_, dc) => {
                const { x, y } = toActual(dc, dr);
                const cell = getCell(x, y);
                const plant = cell.plantId ? plantMap.get(cell.plantId) : null;
                const prevPlant = getPreviousPlant(x, y);
                const hasConflict = toastConflicts.length > 0 &&
                  toastConflicts.some(c =>
                    c.plant1.plantId === cell.plantId || c.plant2.plantId === cell.plantId
                  );
                const hasCompanion = toastCompanions.length > 0 &&
                  toastCompanions.some(c =>
                    c.plant1.plantId === cell.plantId || c.plant2.plantId === cell.plantId
                  );
                const hasRotationIssue = toastRotation.length > 0 &&
                  toastRotation.some(r => r.status === 'bad' && r.plant.plantId === cell.plantId);

                return (
                  <button
                    key={`${x}-${y}`}
                    className={[
                      'grid__cell',
                      plant ? 'grid__cell--planted' : '',
                      hasConflict ? 'grid__cell--conflict' : '',
                      hasRotationIssue ? 'grid__cell--rotation' : '',
                      hasCompanion && !hasConflict && !hasRotationIssue ? 'grid__cell--companion' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleCellClick(x, y)}
                    aria-label={plant ? `${plant.name} at column ${x + 1}, row ${y + 1}` : `Empty cell at column ${x + 1}, row ${y + 1}`}
                    style={plant ? { '--cell-colour': plant.familyColour } as React.CSSProperties : undefined}
                  >
                    {/* Ghost from last season */}
                    {!plant && prevPlant && (
                      <span className="grid__cell-ghost">
                        <span className="grid__cell-ghost-emoji">{plantGlyph(prevPlant)}</span>
                        <span className="grid__cell-ghost-label">Last: {prevPlant.name}</span>
                      </span>
                    )}
                    {plant ? (
                      <>
                        <span className="grid__cell-emoji">{plantGlyph(plant)}</span>
                        <span className="grid__cell-count" title={`Plant ${plant.perSquareFoot} per square`}>
                          {plant.perSquareFoot}
                        </span>
                      </>
                    ) : !prevPlant ? (
                      <span className="grid__cell-plus">+</span>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>
      {pickerCell && (
        <PlantPicker
          plants={plants}
          plantedPlants={plantedPlants}
          previousPlant={pickerPreviousPlant}
          onSelect={handlePlantSelect}
          onClose={() => setPickerCell(null)}
          currentPlantId={currentCell?.plantId ?? null}
        />
      )}
      <ConflictToast
        conflicts={toastConflicts}
        companions={toastCompanions}
        rotationNotes={toastRotation}
        onDismiss={handleDismissToast}
      />
    </div>
  );
}
